<script setup>
import { computed } from 'vue'
import Illustration from './Illustration.vue'
import { ILLUSTRATION_COLOURS } from '@/utils/illustrationColours'
import { SMALL_CARD_SAFE_EDGES } from '@/utils/smallCardIllustrations'

const props = defineProps({
  variation:   { type: String, required: true },  // 'A'..'F' — src/assets/illustrations/small-card-<a-f>.svg
  colour:      { type: String, default: 'pink' },  // a key in ILLUSTRATION_COLOURS, or 'random'
  contentSide: { type: String, default: '' },      // 'left'|'right'|'top'|'bottom' — flips the artwork so a safe edge (see smallCardIllustrations.js) faces this side, when one exists
})

const colourKeys = Object.keys(ILLUSTRATION_COLOURS)

// Re-rolls on every render, including the client's post-hydration render -
// this component isn't wired into any real (prerendered) page yet, only
// ComponentGallery's dev-only demo, so that's harmless here. A component
// that DOES need a stable pick in real prerendered HTML must seed the
// choice instead - see ContentHero.vue and resolveFeatureCardColour's
// seed parameter (src/utils/featureCardIllustrations.js) for why and how.
const resolvedColour = computed(() => (
  props.colour === 'random'
    ? colourKeys[Math.floor(Math.random() * colourKeys.length)]
    : props.colour
))

const set = computed(() => ILLUSTRATION_COLOURS[resolvedColour.value] || ILLUSTRATION_COLOURS.pink)

const illustrationName = computed(() => `small-card-${props.variation.toLowerCase()}`)

const OPPOSITE_EDGE = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' }

// Flip toward contentSide only when the *opposite* edge is the safe one —
// if contentSide is already safe, or neither edge is safe for this axis,
// there's nothing useful to flip.
const flip = computed(() => {
  const safeEdges = SMALL_CARD_SAFE_EDGES[props.variation.toUpperCase()] || []
  if (!props.contentSide || safeEdges.includes(props.contentSide)) return { x: false, y: false }
  if (!safeEdges.includes(OPPOSITE_EDGE[props.contentSide])) return { x: false, y: false }
  const horizontal = props.contentSide === 'left' || props.contentSide === 'right'
  return { x: horizontal, y: !horizontal }
})

const illoStyle = computed(() => ({
  '--illo-1': set.value.colour1,
  '--illo-2': set.value.colour2,
  transform: `scale(${flip.value.x ? -1 : 1}, ${flip.value.y ? -1 : 1})`,
}))
</script>

<template>
  <div class="small-card-tonal-splash" :style="{ background: set.background }">
    <Illustration :name="illustrationName" :style="illoStyle" />
  </div>
</template>

<style lang="scss" scoped>
.small-card-tonal-splash {
  display: block;
  width: 100%;
  // Native small-card viewBox ratio (434x272) — the default when nothing
  // else sizes this box. Callers wanting a different card shape override
  // via a style/class binding (e.g. :style="{ aspectRatio: '1/1' }"); the
  // SVG itself (preserveAspectRatio="xMidYMid slice") always fills whatever
  // box results, cropping rather than letterboxing.
  aspect-ratio: 434 / 272;
  @include rounded($radius);
  overflow: hidden;

  // Same fix as FeatureTonalSplash.vue — stretch Illustration.vue's own
  // inline-block root span so this box's size actually reaches the <svg>.
  :deep(.illustration) {
    display: block;
    width: 100%;
    height: 100%;
  }

  // Overrides Illustration's own default (width:100%, height:auto) — here
  // the box's own size (not the SVG's intrinsic ratio) drives layout, so
  // the SVG must stretch to the box's actual height too. Scoped under
  // .illustration to actually out-specify that default — see
  // FeatureTonalSplash.vue for why a bare `:deep(svg)` isn't enough.
  :deep(.illustration svg) {
    height: 100%;
  }
}
</style>
